/* eslint-disable @typescript-eslint/no-unused-vars */
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Slide,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { TransferOrderPartDetails } from "../../../@types/DRO/GetPartsListResponse";
import {
  SalesOrderDetail,
  SalesOrderLineDTO,
  SalesOrderReturnLineDTO,
} from "../../../@types/DRO/SalesOrderResponse";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { SalesOrderApi } from "../../../api/OrderManagement/SalesOrderApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import {
  AppAutoCompleteWithSearchAPI,
  OptionType,
} from "../../Layouts/AppAutoCompleteWithSearchAPI/AppAutoCompleteWithSearchAPI";

interface SalesOrderItem extends TransferOrderPartDetails {
  productId: number;
  productName: string;
  quantity: number;
  price: number;
  tax: number;
  availableQuantity?: number;
  isOutOfStock?: boolean;
  isNewItem?: boolean;
}

// Transition component for dialog animations
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ViewSalesOrderReturn: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const { setLoading } = useLoading();
  const [orderDetails, setOrderDetails] = useState<SalesOrderDetail | null>(
    null
  );
  const [loadingError, setLoadingError] = useState(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editedQuantities, setEditedQuantities] = useState<{
    [key: string]: string;
  }>({});
  const [isClosing, setIsClosing] = useState<boolean>(false);

  // State for new return items
  const [newReturnItems, setNewReturnItems] = useState<SalesOrderItem[]>([]);
  const [selectedPartIds, setSelectedPartIds] = useState<Set<number>>(
    new Set()
  );
  const [partsRefreshKey, setPartsRefreshKey] = useState(0);
  const [deletedExistingReturnItems, setDeletedExistingReturnItems] = useState<
    Set<number>
  >(new Set());

  // Enhanced notification states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("success");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogSeverity, setDialogSeverity] = useState<"success" | "error">(
    "success"
  );
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<"close" | null>(null);
  const [isProcessingReturn, setIsProcessingReturn] = useState<boolean>(false);

  // Helper function to show snackbar notifications
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Helper function to show dialog notifications
  const showDialog = (
    title: string,
    message: string,
    severity: "success" | "error"
  ) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogSeverity(severity);
    setDialogOpen(true);
  };

  // Extract error message from API response
  const extractErrorMessage = (error: any): string => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    } else if (error.response?.data?.error) {
      return error.response.data.error;
    } else if (error.message) {
      return error.message;
    }
    return "An unexpected error occurred. Please try again.";
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    if (!id) {
      setLoadingError(true);
      showSnackbar("Invalid order ID", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await SalesOrderApi.api.getSingleSalesOrderReturnDetails(
        parseInt(id)
      );
      setOrderDetails(response.data);

      // Initialize edited quantities as strings for return lines
      const quantities: { [key: string]: string } = {};
      response.data.returnLines?.forEach((line: SalesOrderReturnLineDTO) => {
        quantities[`existing-${line.partId}`] = line.quantity.toString();
      });
      setEditedQuantities(quantities);

      // Initialize selected part IDs for return lines
      const partIds = new Set<number>();
      response.data.returnLines?.forEach((line: SalesOrderReturnLineDTO) => {
        partIds.add(line.partId);
      });
      setSelectedPartIds(partIds);
    } catch (error) {
      console.error("Error fetching sales order return details:", error);
      setLoadingError(true);
      const errorMessage = extractErrorMessage(error);
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch parts with warehouse context
  const fetchPaginatedPartsBySearchTerm = useCallback(
    (data: {
      currentPageNumber: number;
      currentSearchQueryTerm: string;
    }): Promise<{
      options: OptionType<SalesOrderItem>[];
      hasMore: boolean;
    }> => {
      if (!orderDetails?.toWarehouseDTO?.deliveryWarehouseId) {
        return Promise.resolve({ options: [], hasMore: false });
      }

      return AllPartApi.api
        .getPartList(
          data.currentPageNumber,
          10,
          data.currentSearchQueryTerm,
          undefined,
          undefined,
          undefined,
          orderDetails.toWarehouseDTO.deliveryWarehouseId,
          undefined,
          true
        )
        .then(({ data: response }) => {
          // Filter out already selected parts
          const availableParts = response.data.filter(
            (part: any) =>
              !selectedPartIds.has(part.id) &&
              !deletedExistingReturnItems.has(part.id)
          );

          return {
            options: availableParts.map((part: any) => ({
              value: part.id.toString(),
              label: part.isOutOfStock
                ? `${part.description} (Out of Stock)`
                : part.description,
              data: {
                ...part,
                productId: part.id,
                productName: part.description,
                quantity: 0,
                price: part.salesPrice,
                tax: part.tax || 0,
                availableQuantity: part.quantity || 0,
                isOutOfStock: part.isOutOfStock || false,
              } as SalesOrderItem,
            })),
            hasMore: data.currentPageNumber < response.totalPages - 1,
          };
        })
        .catch(() => ({ options: [], hasMore: false }));
    },
    [orderDetails, selectedPartIds, deletedExistingReturnItems]
  );

  const calculateTotalCost = (orderLines: SalesOrderLineDTO[]) => {
    let total = 0;

    // Calculate total for existing items (excluding deleted ones)
    orderLines.forEach((line) => {
      if (!deletedExistingReturnItems.has(line.partId)) {
        const quantityStr = editedQuantities[`existing-${line.partId}`];
        const quantity = quantityStr
          ? parseFloat(quantityStr) || 0
          : line.quantity;
        total += quantity * line.price;
      }
    });

    // Calculate total for new items
    newReturnItems.forEach((item) => {
      if (!item.isOutOfStock) {
        const quantityStr = editedQuantities[`new-${item.productId}`];
        const quantity = quantityStr
          ? parseFloat(quantityStr) || 0
          : item.quantity;
        total += quantity * item.price;
      }
    });

    return total;
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Reset quantities to original values for return lines
    if (orderDetails?.returnLines) {
      const quantities: { [key: string]: string } = {};
      orderDetails.returnLines.forEach((line) => {
        quantities[`existing-${line.partId}`] = line.quantity.toString();
      });
      setEditedQuantities(quantities);
    }
    setNewReturnItems([]);
    const partIds = new Set<number>();
    orderDetails?.returnLines?.forEach((line: SalesOrderReturnLineDTO) => {
      partIds.add(line.partId);
    });
    setSelectedPartIds(partIds);
    setDeletedExistingReturnItems(new Set());
  };

  const handleQuantityChange = (key: string, newValue: string) => {
    // Allow empty string for clearing the field
    if (newValue === "") {
      setEditedQuantities((prev) => ({
        ...prev,
        [key]: newValue,
      }));
      return;
    }

    // Check if this is an existing return item
    if (key.startsWith("existing-")) {
      const partId = parseInt(key.replace("existing-", ""));
      const existingItem = orderDetails?.returnLines?.find(
        (item) => item.partId === partId
      );
      if (existingItem) {
        // If partProductType is 'SERVICE', allow 0 and skip availableQuantity check
        if (existingItem.partProductType === "SERVICE") {
          if (!/^\d*\.?\d*$/.test(newValue)) {
            return;
          }
          setEditedQuantities((prev) => ({
            ...prev,
            [key]: newValue,
          }));
          return;
        }
        // For others, keep original logic but remove availableQuantity check
        if (newValue === "0" || (newValue.length === 1 && newValue === "0")) {
          showSnackbar("Minimum quantity should be 1", "error");
          return;
        }
        if (/^[1-9]\d*\.?\d*$/.test(newValue) || newValue === "") {
          setEditedQuantities((prev) => ({
            ...prev,
            [key]: newValue,
          }));
        }
        return;
      }
    }
    // Check if this is a new return item
    if (key.startsWith("new-")) {
      const partId = parseInt(key.replace("new-", ""));
      const newItem = newReturnItems.find((item) => item.productId === partId);
      if (newItem) {
        // If partProductType is 'SERVICE', allow 0 and skip availableQuantity check
        if (newItem.partProductType === "SERVICE") {
          if (!/^\d*\.?\d*$/.test(newValue)) {
            return;
          }
          setEditedQuantities((prev) => ({
            ...prev,
            [key]: newValue,
          }));
          return;
        }
        // For others, keep original logic but remove availableQuantity check
        if (newValue === "0" || (newValue.length === 1 && newValue === "0")) {
          showSnackbar("Minimum quantity should be 1", "error");
          return;
        }
        if (/^[1-9]\d*\.?\d*$/.test(newValue) || newValue === "") {
          setEditedQuantities((prev) => ({
            ...prev,
            [key]: newValue,
          }));
        }
        return;
      }
    }
  };

  const validateQuantities = (): boolean => {
    // Check existing return items
    for (const line of orderDetails?.returnLines || []) {
      if (!deletedExistingReturnItems.has(line.partId)) {
        const quantityStr = editedQuantities[`existing-${line.partId}`] || "";
        // If partProductType is 'SERVICE', allow 0 and skip availableQuantity check
        if (line.partProductType === "SERVICE") {
          if (quantityStr === "" || isNaN(Number(quantityStr))) {
            showSnackbar(`Quantity is required for ${line.partName}`, "error");
            return false;
          }
          continue;
        }
        if (quantityStr === "" || parseFloat(quantityStr) <= 0) {
          showSnackbar(`Quantity is required for ${line.partName}`, "error");
          return false;
        }
      }
    }
    // Check new return items
    for (const item of newReturnItems) {
      if (item.productId > 0 && !item.isOutOfStock) {
        const quantityStr = editedQuantities[`new-${item.productId}`] || "";
        // If partProductType is 'SERVICE', allow 0 and skip availableQuantity check
        if (item.partProductType === "SERVICE") {
          if (quantityStr === "" || isNaN(Number(quantityStr))) {
            showSnackbar(
              `Quantity is required for ${item.productName}`,
              "error"
            );
            return false;
          }
          continue;
        }
        if (quantityStr === "" || parseFloat(quantityStr) <= 0) {
          showSnackbar(`Quantity is required for ${item.productName}`, "error");
          return false;
        }
      }
    }
    return true;
  };

  const handleAddReturnItem = () => {
    const newItem: SalesOrderItem = {
      id: 0,
      productId: 0,
      productName: "",
      quantity: 0,
      price: 0,
      tax: 0,
      externalId: 0,
      referenceCode: "",
      type: "",
      description: "",
      status: 0,
      costPrice: 0,
      averageCostPrice: 0,
      salesPrice: 0,
      penaltyType: "",
      deleted: false,
      updatedBy: 0,
      isNewItem: true,
    };

    setNewReturnItems([...newReturnItems, newItem]);
    // Don't set initial quantity - keep it empty
    setEditedQuantities((prev) => ({
      ...prev,
      [`new-${newItem.productId}`]: "",
    }));
  };

  const handleDeleteExistingReturnItem = (partId: number) => {
    // Add to deleted items set
    setDeletedExistingReturnItems((prev) => {
      const newSet = new Set(prev);
      newSet.add(partId);
      return newSet;
    });

    // Remove from selected parts
    setSelectedPartIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(partId);
      return newSet;
    });

    // Force refresh of all dropdowns
    setPartsRefreshKey((prev) => prev + 1);
  };

  const handleDeleteNewReturnItem = (index: number) => {
    const itemToDelete = newReturnItems[index];

    // Remove the part ID from selected parts
    if (itemToDelete.productId > 0) {
      setSelectedPartIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemToDelete.productId);
        return newSet;
      });

      // Remove from edited quantities
      const newQuantities = { ...editedQuantities };
      delete newQuantities[`new-${itemToDelete.productId}`];
      setEditedQuantities(newQuantities);

      // Force refresh of all dropdowns
      setPartsRefreshKey((prev) => prev + 1);
    }

    const updatedItems = newReturnItems.filter((_, i) => i !== index);
    setNewReturnItems(updatedItems);
  };

  const handleProductSelect =
    (index: number) =>
    (event: any, value: OptionType<SalesOrderItem> | null) => {
      const updatedNewItems = [...newReturnItems];
      const oldPartId = updatedNewItems[index].productId;

      if (value && value.data) {
        // Remove old part ID from selected parts
        if (oldPartId > 0) {
          setSelectedPartIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(oldPartId);
            return newSet;
          });

          // Remove old quantity
          const newQuantities = { ...editedQuantities };
          delete newQuantities[`new-${oldPartId}`];
          setEditedQuantities(newQuantities);
        }

        // Add new part ID to selected parts
        setSelectedPartIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(value.data.productId);
          return newSet;
        });

        // Update item with new part data
        updatedNewItems[index] = {
          ...value.data,
          quantity: 0,
          isNewItem: true,
        };
        setNewReturnItems(updatedNewItems);

        // Set empty quantity for new item
        setEditedQuantities((prev) => ({
          ...prev,
          [`new-${value.data.productId}`]: "",
        }));

        // Force refresh of all dropdowns
        setPartsRefreshKey((prev) => prev + 1);
      } else {
        // Remove old part ID when deselecting
        if (oldPartId > 0) {
          setSelectedPartIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(oldPartId);
            return newSet;
          });

          // Remove quantity
          const newQuantities = { ...editedQuantities };
          delete newQuantities[`new-${oldPartId}`];
          setEditedQuantities(newQuantities);

          // Force refresh of all dropdowns
          setPartsRefreshKey((prev) => prev + 1);
        }

        // Reset the item
        updatedNewItems[index] = {
          id: 0,
          productId: 0,
          productName: "",
          quantity: 0,
          price: 0,
          tax: 0,
          externalId: 0,
          referenceCode: "",
          type: "",
          description: "",
          status: 0,
          costPrice: 0,
          averageCostPrice: 0,
          salesPrice: 0,
          penaltyType: "",
          deleted: false,
          updatedBy: 0,
          isNewItem: true,
        };
        setNewReturnItems(updatedNewItems);
      }
    };

  const handleCloseOrderReturn = async () => {
    if (!orderDetails || !id) return;

    // Validate all quantities before closing
    if (!validateQuantities()) {
      return;
    }

    setIsProcessingReturn(true);
    try {
      // Prepare return lines with edited quantities
      const returnLines: any[] = [];

      // Add existing return items with edited quantities (excluding deleted ones)
      orderDetails.returnLines?.forEach((line) => {
        if (!deletedExistingReturnItems.has(line.partId)) {
          const quantityStr = editedQuantities[`existing-${line.partId}`];
          const quantity = quantityStr
            ? parseFloat(quantityStr) || 0
            : line.quantity;

          if (quantity > 0) {
            returnLines.push({
              partId: line.partId,
              quantity: quantity,
            });
          }
        }
      });

      // Add new return items
      newReturnItems.forEach((item) => {
        if (item.productId > 0 && !item.isOutOfStock) {
          const quantityStr = editedQuantities[`new-${item.productId}`];
          const quantity = quantityStr
            ? parseFloat(quantityStr) || 0
            : item.quantity;

          if (quantity > 0) {
            returnLines.push({
              partId: item.productId,
              quantity: quantity,
            });
          }
        }
      });

      // Prepare API body
      const body = {
        saleId: orderDetails.id,
        products: returnLines,
      };

      await SalesOrderApi.api.createSalesOrderReturn(body);

      const successMessage = "Sales order return processed successfully";
      showDialog("Order Return Processed", successMessage, "success");
      setTimeout(() => {
        navigate(`/return-order${statusFilter ? `?status=${statusFilter}` : ''}`);
      }, 1500);
    } catch (error) {
      console.error("Error processing sales order return:", error);
      const errorMessage = extractErrorMessage(error);
      showDialog("Processing Failed", errorMessage, "error");
    } finally {
      setIsProcessingReturn(false);
      setConfirmDialogOpen(false);
      setActionType(null);
    }
  };

  const handleActionClick = (type: "close") => {
    setActionType(type);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = () => {
    if (actionType === "close") {
      handleCloseOrderReturn();
    }
    // Don't close the modal here
    // setConfirmDialogOpen(false);
    // setActionType(null);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setActionType(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    if (dialogSeverity === "success") {
      // If it was a success, navigate to sales orders
      navigate(`/return-order${statusFilter ? `?status=${statusFilter}` : ''}`);
    }
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const getStatusClass = (status: string): string => {
    const statusClasses: Record<string, string> = {
      PENDING: "bg-yellow-400",
      CLOSED: "bg-gray-400",
      COMPLETED: "bg-green-400",
      ACTIVE: "bg-blue-400",
    };
    return statusClasses[status] || "bg-gray-400";
  };

  const handleBack = () => {
    navigate(`/return-order${statusFilter ? `?status=${statusFilter}` : ''}`);
  };

  if (loadingError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <ErrorIcon sx={{ fontSize: 48, color: "error.main", mb: 2 }} />
          <h2 className="text-xl font-semibold mb-2">Error loading order</h2>
          <p className="text-gray-600 mb-4">
            Unable to fetch order details. Please try again.
          </p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            Back to Sales Orders
          </button>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const isOrderClosed = orderDetails.status === "CLOSED";
  const isOrderPending = orderDetails.status === "PENDING";

  return (
    <Box sx={{ padding: 4 }}>
      {/* Heading */}
      <Typography
        variant="h5"
        gutterBottom
        sx={{ textAlign: "left", fontWeight: "500", color: "#1E1E1E", mb: 2 }}>
        View Sales Order Return
      </Typography>

      {/* Breadcrumbs */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 4,
          backgroundColor: "#F8F9FA",
          padding: "12px 16px",
          borderRadius: "8px",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        }}>
        <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
          <Link
            to="/sales-order"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
            Dashboard
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <Link
            to={`/return-order${statusFilter ? `?status=${statusFilter}` : ''}`}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
            Sales
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <span className="text-sm font-medium text-gray-700">
            View Order Return
          </span>
        </div>
      </Box>

      {/* Status and Order Info */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: "600" }}>
            Sales Order #{orderDetails?.id || "N/A"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Created: {orderDetails?.creationDate || "N/A"}
          </Typography>
        </Box>
        <div
          className={`text-white font-semibold rounded-xl px-2 ${getStatusClass(
            orderDetails.status
          )}`}>
          {orderDetails.status || "N/A"}
        </div>
      </Box>

      {/* Form */}
      <Paper
        sx={{
          padding: 3,
          marginTop: 2,
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}>
        <Grid container spacing={3} sx={{ textAlign: "left" }}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Reference
            </Typography>
            <TextField
              fullWidth
              variant="standard"
              disabled
              value={orderDetails.reference}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Status
            </Typography>
            <TextField
              fullWidth
              value={orderDetails.status}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Source Warehouse
            </Typography>
            <TextField
              fullWidth
              value={
                orderDetails.fromWarehouseDTO?.deliveryWarehouseName || "N/A"
              }
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Destination Warehouse
            </Typography>
            <TextField
              fullWidth
              value={
                orderDetails.toWarehouseDTO?.deliveryWarehouseName || "N/A"
              }
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Warning Status
            </Typography>
            <TextField
              fullWidth
              value={orderDetails.warning ? "Has Warnings" : "No Warnings"}
              variant="standard"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Total Items
            </Typography>
            <TextField
              fullWidth
              value={
                (orderDetails.orderLineDtos?.filter(
                  (line) => !deletedExistingReturnItems.has(line.partId)
                ).length || 0) + newReturnItems.length
              }
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Job ID
            </Typography>
            <TextField
              fullWidth
              value={orderDetails.jobId || "N/A"}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Job Type
            </Typography>
            <TextField
              fullWidth
              value={orderDetails.jobType || "N/A"}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Fleet Name
            </Typography>
            <TextField
              fullWidth
              value={orderDetails.fleetName || "N/A"}
              variant="standard"
              disabled
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Original Items Table */}
      <Box sx={{ marginTop: 4 }}>
        <Typography
          variant="h6"
          sx={{
            textAlign: "left",
            fontWeight: "600",
            color: "#1E1E1E",
            mb: 2,
          }}>
          Original Items
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                <TableCell sx={{ fontWeight: "600" }}>#</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Part ID</TableCell>
                <TableCell sx={{ fontWeight: "600", width: "300px" }}>
                  Product Description
                </TableCell>
                <TableCell sx={{ fontWeight: "600", width: "300px" }}>
                  Reference Code{" "}
                </TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Quantity</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Unit Price</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Total</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Original Items - Read only */}
              {orderDetails.orderLineDtos &&
              orderDetails.orderLineDtos.length > 0 ? (
                orderDetails.orderLineDtos?.map((line, index) => (
                  <TableRow key={`original-${index}`}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>#{line.partId}</TableCell>
                    <TableCell>{line.partName}</TableCell>
                    <TableCell>{line.referenceCode}</TableCell>
                    <TableCell>{line.quantity}</TableCell>
                    <TableCell>₹ {line.price?.toFixed(2) || "0.00"}</TableCell>
                    <TableCell>
                      ₹ {(line.quantity * line.price)?.toFixed(2) || "0.00"}
                    </TableCell>
                    <TableCell>
                      {line.warning ? (
                        <span className="text-amber-600 font-medium">
                          Low Stock
                        </span>
                      ) : (
                        <span className="text-green-600 font-medium">
                          Available
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}>
                    No products found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Return Line Items Table */}
      <Box sx={{ marginTop: 4 }}>
        <Typography
          variant="h6"
          sx={{
            textAlign: "left",
            fontWeight: "600",
            color: "#1E1E1E",
            mb: 2,
          }}>
          Return Line Items
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                <TableCell sx={{ fontWeight: "600" }}>#</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Part ID</TableCell>
                <TableCell sx={{ fontWeight: "600", width: "300px" }}>
                  Product Description
                </TableCell>
                <TableCell sx={{ fontWeight: "600", width: "300px" }}>
                  Reference Code{" "}
                </TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Quantity</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Unit Price</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Total</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Status</TableCell>
                {isEditMode && (
                  <TableCell sx={{ fontWeight: "600" }}>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Existing Return Items */}
              {orderDetails.returnLines && orderDetails.returnLines.length > 0
                ? orderDetails.returnLines?.map((line, index) => {
                    if (deletedExistingReturnItems.has(line.partId)) {
                      return null; // Don't render deleted items
                    }

                    const quantityKey = `existing-${line.partId}`;
                    const currentQuantity =
                      editedQuantities[quantityKey] !== undefined
                        ? editedQuantities[quantityKey]
                        : line.quantity.toString();
                    const numericQuantity = parseFloat(currentQuantity) || 0;
                    const availableQty = line.availableQuantity || 0;

                    return (
                      <TableRow key={`existing-return-${index}`}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>#{line.partId}</TableCell>
                        <TableCell>{line.partName}</TableCell>
                        <TableCell>{line.referenceCode}</TableCell>

                        <TableCell>
                          {isEditMode ? (
                            <TextField
                              value={currentQuantity}
                              onChange={(e) =>
                                handleQuantityChange(
                                  quantityKey,
                                  e.target.value
                                )
                              }
                              size="small"
                              placeholder="Enter qty"
                              sx={{
                                width: "100px",
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                },
                              }}
                              inputProps={{
                                max: availableQty,
                              }}
                            />
                          ) : (
                            numericQuantity
                          )}
                        </TableCell>
                        <TableCell>
                          ₹ {line.price?.toFixed(2) || "0.00"}
                        </TableCell>
                        <TableCell>
                          ₹{" "}
                          {(numericQuantity * line.price)?.toFixed(2) || "0.00"}
                        </TableCell>
                        <TableCell>
                          {line.warning ? (
                            <span className="text-amber-600 font-medium">
                              Low Stock
                            </span>
                          ) : (
                            <span className="text-green-600 font-medium">
                              Available
                            </span>
                          )}
                        </TableCell>
                        {isEditMode && (
                          <TableCell>
                            <IconButton
                              onClick={() =>
                                handleDeleteExistingReturnItem(line.partId)
                              }
                              sx={{
                                color: "#FF0000",
                              }}
                              size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                : !isEditMode &&
                  newReturnItems.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        sx={{ textAlign: "center", py: 4 }}>
                        No return items found
                      </TableCell>
                    </TableRow>
                  )}

              {/* New Return Items */}
              {isEditMode &&
                newReturnItems.map((item, index) => {
                  const isOutOfStock = item.isOutOfStock || false;
                  const availableQty = item.availableQuantity || 0;
                  const quantityKey = `new-${item.productId}`;
                  const currentQuantity = editedQuantities[quantityKey] || "";
                  const numericQuantity = parseFloat(currentQuantity) || 0;
                  const visibleExistingItems =
                    orderDetails.returnLines?.filter(
                      (line) => !deletedExistingReturnItems.has(line.partId)
                    ).length || 0;
                  const rowIndex = visibleExistingItems + index + 1;

                  return (
                    <TableRow
                      key={`new-return-${index}`}
                      sx={{
                        backgroundColor: isOutOfStock ? "#f5f5f5" : "inherit",
                        opacity: isOutOfStock ? 0.6 : 1,
                      }}>
                      <TableCell>{rowIndex}</TableCell>
                      <TableCell>
                        {item.productId > 0 ? `#${item.productId}` : "-"}
                      </TableCell>
                      <TableCell>
                        <AppAutoCompleteWithSearchAPI
                          key={`part-select-${index}-warehouse-${
                            orderDetails?.toWarehouseDTO?.deliveryWarehouseId ||
                            "none"
                          }-refresh-${partsRefreshKey}`}
                          initialPageNumber={0}
                          value={
                            item.productId > 0
                              ? {
                                  value: item.productId.toString(),
                                  label: item.isOutOfStock
                                    ? `${item.productName} (Out of Stock)`
                                    : item.productName,
                                  data: item,
                                }
                              : null
                          }
                          onChange={handleProductSelect(index)}
                          placeholder="Select product"
                          getData={fetchPaginatedPartsBySearchTerm}
                        />
                      </TableCell>
                      <TableCell>{item.referenceCode}</TableCell>
                      <TableCell>
                        {item.productId > 0 ? (
                          <TextField
                            value={currentQuantity}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              handleQuantityChange(quantityKey, newValue);
                            }}
                            size="small"
                            placeholder="Enter qty"
                            disabled={!item.productName || isOutOfStock}
                            sx={{
                              width: "100px",
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontSize: "14px",
                                fontWeight: 500,
                              },
                            }}
                            inputProps={{
                              max: availableQty,
                            }}
                            FormHelperTextProps={{
                              sx: {
                                color:
                                  numericQuantity > availableQty
                                    ? "error.main"
                                    : "text.secondary",
                              },
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {item.productId > 0
                          ? `₹ ${item.price?.toFixed(2) || "0.00"}`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {item.productId > 0 && !isOutOfStock
                          ? `₹ ${
                              (numericQuantity * item.price)?.toFixed(2) ||
                              "0.00"
                            }`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {item.productId > 0 ? (
                          isOutOfStock ? (
                            <span className="text-red-600 font-medium">
                              Out of Stock
                            </span>
                          ) : numericQuantity > availableQty ? (
                            <span className="text-amber-600 font-medium">
                              Exceeds Stock
                            </span>
                          ) : (
                            <span className="text-green-600 font-medium">
                              Available ({availableQty})
                            </span>
                          )
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleDeleteNewReturnItem(index)}
                          sx={{
                            color: "#FF0000",
                          }}
                          size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add Return Item Button */}
        {isEditMode && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <IconButton
              onClick={handleAddReturnItem}
              sx={{
                color: "#1976D2",
                mr: 1,
              }}
              size="small">
              <AddIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="body1"
              sx={{
                cursor: "pointer",
                color: "#1976D2",
                fontSize: "0.875rem",
              }}
              onClick={handleAddReturnItem}>
              Add Return Item
            </Typography>
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          marginTop: 4,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}>
        {isOrderPending && !isEditMode && (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEditClick}
              sx={{
                padding: "10px 20px",
                borderRadius: "32px",
                textTransform: "none",
                fontWeight: "500",
              }}>
              Edit Return
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleActionClick("close")}
              sx={{
                padding: "10px 20px",
                borderRadius: "32px",
                textTransform: "none",
                fontWeight: "500",
              }}>
              Process Return
            </Button>
          </>
        )}

        {isEditMode && (
          <>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCancelEdit}
              sx={{
                padding: "10px 20px",
                borderRadius: "32px",
                textTransform: "none",
                fontWeight: "500",
              }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleActionClick("close")}
              sx={{
                padding: "10px 20px",
                borderRadius: "32px",
                textTransform: "none",
                fontWeight: "500",
              }}>
              Process Return
            </Button>
          </>
        )}

        {!isEditMode && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleBack}
            sx={{
              padding: "10px 20px",
              borderRadius: "32px",
              textTransform: "none",
              fontWeight: "500",
            }}>
            Back to Sales Orders
          </Button>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={isProcessingReturn ? undefined : handleCloseConfirmDialog}
        TransitionComponent={Transition}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
          },
        }}>
        <DialogTitle
          id="confirm-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pb: 1,
            fontWeight: 600,
            fontSize: 20,
          }}>
          <ErrorIcon sx={{ color: "warning.main", fontSize: 32 }} />
          Process Sales Order Return
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="confirm-dialog-description"
            sx={{
              color: "text.primary",
              fontSize: "16px",
              mt: 1,
              mb: 2,
              textAlign: "left",
            }}>
            {isEditMode
              ? "Are you sure you want to process this sales order return? All changes will be saved and this action cannot be undone."
              : "Are you sure you want to process this sales order return? This action cannot be undone."}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, pb: 2, justifyContent: "flex-end", gap: 2 }}>
          <Button
            onClick={handleCloseConfirmDialog}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2, minWidth: 100 }}
            disabled={isProcessingReturn}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color="error"
            variant="contained"
            sx={{ borderRadius: 2, minWidth: 140, fontWeight: 600 }}
            autoFocus
            disabled={isProcessingReturn}
            startIcon={
              isProcessingReturn ? (
                <span
                  className="loader"
                  style={{
                    width: 18,
                    height: 18,
                    border: "2px solid #fff",
                    borderTop: "2px solid #f44336",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 1s linear infinite",
                  }}
                />
              ) : null
            }>
            {isProcessingReturn ? "Processing..." : "Yes, Process Return"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Result Dialog with Icons */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        TransitionComponent={Transition}
        aria-labelledby="result-dialog-title"
        aria-describedby="result-dialog-description"
        maxWidth="sm"
        fullWidth>
        <DialogTitle
          id="result-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pb: 1,
          }}>
          {dialogSeverity === "success" ? (
            <CheckCircleIcon sx={{ color: "success.main", fontSize: 28 }} />
          ) : (
            <ErrorIcon sx={{ color: "error.main", fontSize: 28 }} />
          )}
          {dialogTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="result-dialog-description"
            sx={{
              color:
                dialogSeverity === "success" ? "text.primary" : "error.main",
              fontSize: "16px",
            }}>
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDialog}
            color="primary"
            variant="contained"
            sx={{ minWidth: 100 }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for quick notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            boxShadow: 3,
            "& .MuiAlert-icon": {
              fontSize: 26,
            },
          }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseSnackbar}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewSalesOrderReturn;
